import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, CardSubtitle, Row, Badge } from "reactstrap";
import { Colxx } from "../../components/common/CustomBootstrap";
import { AddCommaNumber } from "../../helpers/Utils";
import classNames from "classnames";
import { Link } from "react-router-dom";
import ProductImage from "../../components/common/ProductImage";
import { isC } from "../../helpers/Roles";
import IntlMessages from "../../helpers/IntlMessages";

const PRODUCTS_PER_PAGE = 20;

const ListMenu = (props) => {
  const {
    menus,
    listsData,
    loading,
    onClickImage,
    onToggleDetail,
    activeIndex,
    onOpenAddProductModal,
    searchKeyword,
    wasInitiallyEmpty = false,
  } = props;
  const canCreate = isC("product");
  const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_PAGE);
  const loadMoreRef = useRef(null);

  const data = useMemo(
    () =>
      listsData?.length
        ? listsData
        : menus?.length > 1 && menus?.[0]?.id === 0
          ? menus.slice(1)
          : menus || [],
    [listsData, menus],
  );

  const totalProducts = useMemo(
    () =>
      data.reduce(
        (total, menu) =>
          total + (Array.isArray(menu?.data) ? menu.data.length : 0),
        0,
      ),
    [data],
  );

  const visibleData = useMemo(() => {
    let remaining = visibleCount;

    return data.reduce((menusToDisplay, menu) => {
      if (remaining <= 0 || !Array.isArray(menu?.data)) {
        return menusToDisplay;
      }

      const products = menu.data.slice(0, remaining);
      remaining -= products.length;

      if (products.length > 0) {
        menusToDisplay.push({
          ...menu,
          data: products,
        });
      }

      return menusToDisplay;
    }, []);
  }, [data, visibleCount]);

  const hasMoreProducts = visibleCount < totalProducts;

  useEffect(() => {
    setVisibleCount(PRODUCTS_PER_PAGE);
  }, [data]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (
      !hasMoreProducts ||
      !target ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        observer.disconnect();
        setVisibleCount((currentCount) =>
          Math.min(currentCount + PRODUCTS_PER_PAGE, totalProducts),
        );
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMoreProducts, totalProducts, visibleCount]);

  const calculateVariantPriceRange = (variants) => {
    if (!variants || variants.length === 0) {
      return null;
    }

    let minPrice = Infinity;
    let maxPrice = -Infinity;
    let minPromoPrice = Infinity;
    let maxPromoPrice = -Infinity;

    let hasActivePromotion = false;

    // Lấy ngày hiện tại và set giờ về 0 để so sánh ngày chính xác
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    variants.forEach((variant) => {
      const price = parseFloat(variant.price);
      const promoPrice = parseFloat(variant.promo_price);

      // Cập nhật dải giá gốc
      if (!isNaN(price)) {
        if (price < minPrice) minPrice = price;
        if (price > maxPrice) maxPrice = price;
      }

      let isPromoActive = false;

      if (!isNaN(promoPrice) && promoPrice > 0) {
        const startDate = variant.date_start
          ? new Date(variant.date_start)
          : null;
        const endDate = variant.date_end ? new Date(variant.date_end) : null;

        if (startDate) startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(0, 0, 0, 0);

        // Case 1: Có cả ngày bắt đầu và kết thúc
        if (startDate && endDate) {
          if (today >= startDate && today <= endDate) {
            isPromoActive = true;
          }
        }
        // Case 2: Chỉ có ngày bắt đầu, không có ngày kết thúc
        else if (startDate && !endDate) {
          if (today.getTime() === startDate.getTime()) {
            isPromoActive = true;
          }
        }
        // Case 3: Không có ngày bắt đầu/kết thúc nhưng có giá KM
        else if (!startDate && !endDate) {
          isPromoActive = true;
        }
      }

      if (isPromoActive) {
        hasActivePromotion = true;
        if (promoPrice < minPromoPrice) minPromoPrice = promoPrice;
        if (promoPrice > maxPromoPrice) maxPromoPrice = promoPrice;
      }
    });

    // Nếu không tìm thấy khuyến mãi nào đang hoạt động, trả về null cho dải giá KM
    if (!hasActivePromotion) {
      minPromoPrice = null;
      maxPromoPrice = null;
    }

    return {
      minPrice: minPrice === Infinity ? null : minPrice,
      maxPrice: maxPrice === -Infinity ? null : maxPrice,
      minPromoPrice,
      maxPromoPrice,
    };
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <Fragment>
      <Row>
        {canCreate &&
          !searchKeyword &&
          (activeIndex === 99 || activeIndex === undefined) &&
          wasInitiallyEmpty && (
            <Colxx
              xxs="6"
              md="4"
              lg="4"
              xl="4"
              xxl="3"
              className="pad-bot d-flex"
            >
              <Card
                className="card-hover w-100 cursor-pointer d-flex justify-content-center align-items-center list-menu-card"
                style={{
                  border: "2px dashed #ced4da",
                  minHeight: "220px",
                  borderRadius: "0.75rem",
                  background: "transparent",
                }}
                onClick={onOpenAddProductModal}
              >
                <CardBody className="d-flex flex-column justify-content-center align-items-center text-muted">
                  <i
                    className="simple-icon-plus mb-2"
                    style={{ fontSize: "2rem" }}
                  />
                  <span className="font-weight-bold">Thêm sản phẩm</span>
                </CardBody>
              </Card>
            </Colxx>
          )}
        {totalProducts > 0
          ? visibleData.map((menu, index) => {
              return (
                menu?.data?.length > 0 && (
                  <Fragment key={menu.id || menu.name || index}>
                    {menu?.data &&
                      menu?.data?.map((product, idx) => {
                        // console.log("Rendering product:", product);
                        let priceDisplayContent = null;
                        const hasVariants =
                          product.variants && product.variants.length > 0;
                        const hasSizes =
                          product.size && product.size.length > 0;

                        const bt = String(product.business_type || "")
                          .toLowerCase()
                          .trim();
                        const isFnB = bt === "cafe" || bt === "restaurant";
                        const isFood = product.fnb_type === "food";
                        const hasWholesale =
                          !isFnB && parseFloat(product.price_wholesale) > 0;
                        const hasFnbOptions =
                          isFnB &&
                          (Boolean(product.flavor?.length) ||
                            Boolean(product.size?.length) ||
                            Boolean(product.topping?.length) ||
                            (!isFood &&
                              (Boolean(product.ice?.length) ||
                                Boolean(product.sugar?.length))));
                        const shouldOpenDetail =
                          hasVariants || hasFnbOptions || hasWholesale;

                        // Nếu có variants hoặc có sizes (mô hinh FnB)
                        if (hasVariants || hasSizes) {
                          let minVal = Infinity;
                          let maxVal = -Infinity;
                          let minPromo = Infinity;
                          let maxPromo = -Infinity;
                          let hasActivePromo = false;

                          if (hasVariants) {
                            const range = calculateVariantPriceRange(
                              product.variants,
                            );
                            if (range) {
                              minVal = range.minPrice;
                              maxVal = range.maxPrice;
                              if (
                                range.minPromoPrice !== null &&
                                range.maxPromoPrice !== null
                              ) {
                                minPromo = range.minPromoPrice;
                                maxPromo = range.maxPromoPrice;
                                hasActivePromo = true;
                              }
                            }
                          } else if (hasSizes) {
                            // Mô hình FnB simple sizes
                            product.size.forEach((s) => {
                              const p = parseFloat(s.value);
                              if (!isNaN(p)) {
                                if (p < minVal) minVal = p;
                                if (p > maxVal) maxVal = p;
                              }
                              const pp = parseFloat(s.promo_price);
                              if (!isNaN(pp) && pp > 0) {
                                if (pp < minPromo) minPromo = pp;
                                if (pp > maxPromo) maxPromo = pp;
                                hasActivePromo = true;
                              }
                            });
                          }

                          if (minVal !== Infinity) {
                            const priceRangeText =
                              minVal === maxVal
                                ? AddCommaNumber(minVal)
                                : `${AddCommaNumber(minVal)} - ${AddCommaNumber(
                                    maxVal,
                                  )}`;

                            if (hasActivePromo && minPromo !== Infinity) {
                              const promoRangeText =
                                minPromo === maxPromo
                                  ? AddCommaNumber(minPromo)
                                  : `${AddCommaNumber(
                                      minPromo,
                                    )} - ${AddCommaNumber(maxPromo)}`;
                              priceDisplayContent = (
                                <div className="d-flex flex-column">
                                  <Badge
                                    color="light"
                                    pill
                                    className="mb-1 align-self-start"
                                  >
                                    <del>{priceRangeText}</del>
                                  </Badge>
                                  <Badge
                                    color="primary"
                                    pill
                                    className="align-self-start"
                                  >
                                    {promoRangeText}
                                  </Badge>
                                </div>
                              );
                            } else {
                              priceDisplayContent = (
                                <Badge color="primary" pill>
                                  {priceRangeText}
                                </Badge>
                              );
                            }
                          }
                        }

                        // Fallback cho sản phẩm đơn lẻ hoặc không tính được dải giá
                        if (!priceDisplayContent) {
                          const basePrice = parseFloat(product.price) || 0;
                          const salePrice = parseFloat(product.price_sale) || 0;
                          const hasActiveSale =
                            (product.active_sale === true ||
                              product.active_sale === 1 ||
                              product.active_sale === "1" ||
                              product.active_sale === "true") &&
                            salePrice > 0;

                          priceDisplayContent = (
                            <>
                              {hasActiveSale ? (
                                <>
                                  <Badge color="light" pill className="mr-1">
                                    <del>{AddCommaNumber(basePrice)}</del>
                                  </Badge>
                                  <Badge color="primary" pill>
                                    {AddCommaNumber(salePrice)}
                                  </Badge>
                                </>
                              ) : (
                                <Badge color="primary" pill>
                                  {AddCommaNumber(basePrice)}
                                </Badge>
                              )}
                            </>
                          );
                        }

                        return (
                          <Colxx
                            key={product.id || `${menu.id || index}-${idx}`}
                            xxs="6"
                            md="4"
                            lg="4"
                            xl="4"
                            xxl="3"
                            className="pad-bot d-flex" // Thêm mb-4 để có khoảng cách
                          >
                            <Card
                              className={classNames(
                                "card-hover w-100 list-menu-card", // Thêm w-100 và list-menu-card
                                {
                                  disable: product.softHide ? "disable" : "",
                                },
                              )}
                            >
                              <div className="position-relative cursor-body-menu">
                                <ProductImage
                                  image={product.image}
                                  alt={product.name}
                                  fnbType={product.fnb_type}
                                  className="card-img-top"
                                  iconSize="50px"
                                  onClick={
                                    product.softHide
                                      ? null
                                      : shouldOpenDetail
                                        ? () => onToggleDetail(product)
                                        : () => onClickImage(product)
                                  }
                                />
                                {product.isTemplate && (
                                  <Badge
                                    className="position-absolute badge-top-left"
                                    color={"info"}
                                    pill
                                    style={{
                                      zIndex: 2,
                                      fontSize: "0.72rem",
                                      fontWeight: "600",
                                    }}
                                  >
                                    <IntlMessages id="template.badge" />
                                  </Badge>
                                )}
                                {product.bestter && (
                                  <Badge
                                    className="position-absolute badge-top-right"
                                    color={"success"}
                                    pill
                                  >
                                    {"Khuyên dùng"}
                                  </Badge>
                                )}
                              </div>
                              <div
                                className="d-flex justify-content-around flex-grow-1"
                                onClick={
                                  shouldOpenDetail
                                    ? () => onToggleDetail(product)
                                    : () => onClickImage(product)
                                }
                              >
                                <CardBody
                                  className={classNames(
                                    "d-flex flex-column p-3",
                                    {
                                      "cursor-pointer": shouldOpenDetail,
                                    },
                                  )}
                                >
                                  <CardSubtitle
                                    className="w-100"
                                    title={product.name}
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {/* {product.isTemplate ? (
                                    <span
                                      className="badge badge-info mr-1"
                                      style={{
                                        fontSize: "0.65rem",
                                        verticalAlign: "middle",
                                        padding: "2px 5px",
                                        borderRadius: "4px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      <IntlMessages id="template.badge" />
                                    </span>
                                  ) : null} */}
                                    {product.name}
                                  </CardSubtitle>

                                  {product.softHide ? (
                                    <Colxx className="col-3"></Colxx>
                                  ) : (
                                    <div className="d-flex text-muted text-small mb-0 font-weight-light mt-auto">
                                      {priceDisplayContent}
                                    </div>
                                  )}
                                </CardBody>
                                {shouldOpenDetail ? (
                                  <i className="iconsminds-arrow-right h-100 d-flex align-items-center pr-1" />
                                ) : null}
                              </div>
                            </Card>
                          </Colxx>
                        );
                      })}
                  </Fragment>
                )
              );
            })
          : !(
              canCreate &&
              !searchKeyword &&
              (activeIndex === 99 || activeIndex === undefined) &&
              wasInitiallyEmpty
            ) && (
              <div className="d-flex flex-column justify-content-center align-items-center w-100 py-5">
                <i className="simple-icon-basket-loaded fz-3rem mb-3 text-muted" />
                {props.searchKeyword ? (
                  <p className="text-muted text-center">
                    Không có sản phẩm nào phù hợp với tìm kiếm "
                    <strong>{props.searchKeyword}</strong>" của bạn!
                  </p>
                ) : (
                  <div className="text-muted text-center">
                    {menus && menus.length > 0 ? (
                      <p>Danh mục này hiện không có sản phẩm nào.</p>
                    ) : (
                      ""
                    )}
                  </div>
                )}
              </div>
            )}
      </Row>
      {hasMoreProducts && (
        <div
          ref={loadMoreRef}
          className="d-flex justify-content-center align-items-center py-3 text-muted"
          aria-live="polite"
        >
          Đang tải thêm sản phẩm...
        </div>
      )}
    </Fragment>
  );
};

export default React.memo(ListMenu);
